/**
 * The sidebar, as data: which destinations exist, in which group, in which order.
 *
 * Grouped by the JOB an operator is doing rather than by when each tool was built, because a flat
 * list of a dozen entries read as "everything in one place" and had to be searched every time:
 *
 * - **Support** — working one ticket and the one shop behind it. Daily, so it comes first, and the
 *   Shop-Akte sits right under the tickets because "open this shop" is almost always the next step.
 * - **All merchants** — actions whose blast radius is every shop at once. A group of their own, so
 *   the heading says so before the click.
 * - **Business** — the COO/CFO view of our own company (P&L, growth, unit economics). About the
 *   company, not about a merchant or the system. The figures the team enters for it (costs, plan,
 *   cash, rates) are the dashboard's settings sub-page, not an entry of their own.
 * - **System** — queue health, the servers and the database behind them (Infrastruktur), and failed
 *   jobs. Incident work; the badges say when it is needed.
 * - The **footer** — rare (the audit trail, the team's accounts) or personal (one's own email
 *   signature and password).
 *
 * The four queue entries the sidebar used to carry are gone on purpose: every queue is one click
 * away from the Systemstatus table, and four raw queue names were the noisiest part of the list.
 *
 * JSX-free on purpose, like the rest of `src/lib`: the dashboard's Jest runs in `node` with no
 * jsdom, so the structure is only testable out here. `Layout.tsx` only draws it.
 */
import { SHOP_FILE_BASE } from './shop-file';
import { DOCUMENT_LOOKUP_PATH } from './document-lookup';
import { OPERATIONS_PATH, OPERATIONS_SETTINGS_PATH } from './operations';
import { INFRASTRUCTURE_PATH } from './infrastructure';

/** The icons the sidebar uses — a subset of `ICONS` in `components/Brand.tsx`. */
export type NavIcon =
  | 'chat'
  | 'folder'
  | 'search'
  | 'document'
  | 'megaphone'
  | 'tag'
  | 'chart'
  | 'warning'
  | 'shield'
  | 'mail'
  | 'trend'
  | 'server'
  | 'users'
  | 'key';

/** A live count next to an entry. Which number each one shows is decided in `Layout.tsx`. */
export type NavBadge = 'openTickets' | 'failedJobs';

/** One destination in the sidebar. */
export interface NavEntry {
  readonly to: string;
  readonly labelKey: string;
  readonly icon: NavIcon;
  readonly badge?: NavBadge;
}

/** A labelled group of destinations. */
export interface NavGroup {
  readonly id: 'support' | 'merchants' | 'business' | 'system';
  readonly labelKey: string;
  readonly entries: readonly NavEntry[];
}

/** Where a signed-in operator lands: the ticket queue, because support is the daily job. */
export const HOME_PATH = '/support';

/** The groups, in display order. */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'support',
    labelKey: 'shell.groups.support',
    entries: [
      {
        to: '/support',
        labelKey: 'shell.nav.tickets',
        icon: 'chat',
        badge: 'openTickets',
      },
      { to: SHOP_FILE_BASE, labelKey: 'shell.nav.shopFile', icon: 'folder' },
      {
        to: DOCUMENT_LOOKUP_PATH,
        labelKey: 'shell.nav.documents',
        icon: 'search',
      },
      {
        to: '/canned-responses',
        labelKey: 'shell.nav.cannedResponses',
        icon: 'document',
      },
    ],
  },
  {
    id: 'merchants',
    labelKey: 'shell.groups.merchants',
    entries: [
      {
        to: '/broadcast-alert',
        labelKey: 'shell.nav.broadcast',
        icon: 'megaphone',
      },
      { to: '/vouchers', labelKey: 'shell.nav.vouchers', icon: 'tag' },
    ],
  },
  {
    // The COO/CFO view of our own business: P&L, growth, unit economics. Its own group because it
    // is about the company, not about any merchant or the system. Its settings sub-page (costs,
    // plan, …) has no entry: the dashboard links it, and this entry stays active while it is open
    // (`NavLink` matches `/operations/*`).
    id: 'business',
    labelKey: 'shell.groups.business',
    entries: [
      { to: OPERATIONS_PATH, labelKey: 'shell.nav.operations', icon: 'trend' },
    ],
  },
  {
    id: 'system',
    labelKey: 'shell.groups.system',
    entries: [
      { to: '/', labelKey: 'shell.nav.systemStatus', icon: 'chart' },
      {
        to: INFRASTRUCTURE_PATH,
        labelKey: 'shell.nav.infrastructure',
        icon: 'server',
      },
      {
        to: '/failed-jobs',
        labelKey: 'shell.nav.failedJobs',
        icon: 'warning',
        badge: 'failedJobs',
      },
    ],
  },
];

/** The entries pinned to the bottom of the sidebar, below a divider and without a heading. */
export const NAV_FOOTER: readonly NavEntry[] = [
  { to: '/audit-log', labelKey: 'shell.nav.auditLog', icon: 'shield' },
  { to: '/signature', labelKey: 'shell.nav.signature', icon: 'mail' },
  { to: '/team', labelKey: 'shell.nav.team', icon: 'users' },
  { to: '/account', labelKey: 'shell.nav.account', icon: 'key' },
];

/**
 * Routes that deliberately have NO sidebar entry, because they are reached from another screen:
 * a queue from the Systemstatus table, a ticket from the queue, a shop's file from the Shop-Akte
 * search, the operations dashboard's settings from the dashboard's header button. Listed so the spec can tell "reached another way" from "forgotten".
 */
export const ROUTES_WITHOUT_ENTRY: readonly string[] = [
  '/queue/:name',
  '/support/:id',
  `${SHOP_FILE_BASE}/:shopId`,
  OPERATIONS_SETTINGS_PATH,
];

/** Every entry, groups first, then the footer — in the order they are drawn. */
export function allNavEntries(): NavEntry[] {
  return [...NAV_GROUPS.flatMap((group) => group.entries), ...NAV_FOOTER];
}

/**
 * The text a badge shows, or `null` for none. A zero shows nothing — a badge that is always there
 * stops being read — and anything past 99 is `99+`, so a bad night cannot widen the sidebar.
 */
export function badgeText(count: number | undefined): string | null {
  if (count === undefined || !Number.isFinite(count) || count <= 0) {
    return null;
  }
  return count > 99 ? '99+' : String(Math.floor(count));
}
